/**
 * Status turnamen — satu sumber kebenaran untuk seluruh antarmuka.
 *
 * Empat keadaan, masing-masing menentukan dua hal:
 *
 *   dibuka        terlihat, tombol daftar ADA
 *   segera_hadir  terlihat, tombol daftar TIDAK ADA
 *   selesai       terlihat, tombol daftar TIDAK ADA
 *   sembunyikan   tidak pernah sampai ke sini — sudah disaring backend
 *
 * Backend mengirim `status_normal` dan `bisa_daftar` yang sudah dinormalkan.
 * Fungsi di bawah tetap menyediakan penerjemahan sendiri sebagai cadangan,
 * untuk data yang datang dari endpoint lama yang belum ikut diperbarui.
 */

export const STATUS = {
  DIBUKA: 'dibuka',
  SEGERA: 'segera_hadir',
  DITUTUP: 'ditutup',
  SELESAI: 'selesai',
  SEMBUNYIKAN: 'sembunyikan',
};

const ALIAS = {
  active: STATUS.DIBUKA,
  buka_pendaftaran: STATUS.DIBUKA,
  pendaftaran: STATUS.DIBUKA,
  open: STATUS.DIBUKA,

  ditutup: STATUS.DITUTUP,
  tutup: STATUS.DITUTUP,
  closed: STATUS.DITUTUP,
  draft: STATUS.SEGERA,
  coming_soon: STATUS.SEGERA,
  segera: STATUS.SEGERA,

  finished: STATUS.SELESAI,
  completed: STATUS.SELESAI,

  inactive: STATUS.SEMBUNYIKAN,
  hidden: STATUS.SEMBUNYIKAN,
  nonaktif: STATUS.SEMBUNYIKAN,
};

/** Menormalkan nilai status apa pun ke salah satu dari empat status resmi. */
export function normalkanStatus(nilai) {
  const s = String(nilai || '').toLowerCase().trim();
  if (Object.values(STATUS).includes(s)) return s;
  return ALIAS[s] || STATUS.SEGERA;
}

/**
 * Informasi tampilan untuk sebuah turnamen.
 * Mengembalikan kunci terjemahan, bukan teks jadi, supaya lencana ikut
 * berganti bahasa bersama sisa halaman.
 */
export function infoStatus(turnamen) {
  const s = turnamen?.status_normal
    ? normalkanStatus(turnamen.status_normal)
    : normalkanStatus(turnamen?.status);

  const peta = {
    [STATUS.DIBUKA]: {
      kunci: 'status.dibuka',
      tone: 'success',
      ikon: 'bi bi-broadcast',
      bisaDaftar: true,
      terlihat: true,
    },
    [STATUS.SEGERA]: {
      kunci: 'status.segeraHadir',
      tone: 'warning',
      ikon: 'bi bi-hourglass-split',
      bisaDaftar: false,
      terlihat: true,
    },
    [STATUS.DITUTUP]: {
      kunci: 'status.ditutup',
      tone: 'warning',
      ikon: 'bi bi-lock',
      bisaDaftar: false,
      terlihat: true,
    },
    [STATUS.SELESAI]: {
      kunci: 'status.selesai',
      tone: 'neutral',
      ikon: 'bi bi-flag-fill',
      bisaDaftar: false,
      terlihat: true,
    },
    [STATUS.SEMBUNYIKAN]: {
      kunci: 'status.sembunyikan',
      tone: 'neutral',
      ikon: 'bi bi-eye-slash',
      bisaDaftar: false,
      terlihat: false,
    },
  };

  const info = peta[s];

  // Bila backend sudah mengirim bisa_daftar, nilainya yang dipakai —
  // server adalah pihak yang berwenang menentukan hal ini.
  const dasar = typeof turnamen?.bisa_daftar === 'boolean'
    ? { ...info, status: s, bisaDaftar: turnamen.bisa_daftar }
    : { ...info, status: s };

  /*
   * Lencana "Sedang Berlangsung" menimpa lencana lain selama acaranya
   * memang berjalan hari ini. Ini keterangan yang paling dicari pengunjung,
   * dan menampilkan "Segera Hadir" pada acara yang partainya sudah jalan
   * membuat situs terlihat tidak terurus.
   *
   * Kemampuan mendaftar TIDAK ikut berubah — itu tetap urusan panitia.
   */
  // Kuota atlet event sudah penuh: pendaftaran otomatis tertutup.
  if (s !== STATUS.SELESAI && s !== STATUS.SEMBUNYIKAN && turnamen?.kuota_atlet_penuh) {
    return { ...dasar, kunci: 'status.kuotaPenuh', tone: 'danger', ikon: 'bi bi-people-fill', bisaDaftar: false, berlangsung: sedangBerlangsung(turnamen) };
  }

  // Ditutup admin, atau status "dibuka" tapi batas waktu pendaftaran sudah lewat.
  if (s === STATUS.DITUTUP || (s === STATUS.DIBUKA && turnamen?.bisa_daftar === false)) {
    return {
      ...dasar,
      kunci: 'status.ditutup',
      tone: 'warning',
      ikon: 'bi bi-lock',
      berlangsung: false,
    };
  }

  if (s !== STATUS.SELESAI && s !== STATUS.SEMBUNYIKAN && sedangBerlangsung(turnamen)) {
    return {
      ...dasar,
      kunci: 'status.berlangsung',
      tone: 'danger',
      ikon: 'bi bi-broadcast-pin',
      berlangsung: true,
    };
  }

  return { ...dasar, berlangsung: false };
}

/** Menyaring turnamen yang tidak boleh tampil di halaman publik. */
export function hanyaTerlihat(daftar) {
  if (!Array.isArray(daftar)) return [];
  return daftar.filter((t) => infoStatus(t).terlihat);
}

/**
 * Apakah acaranya sedang berjalan hari ini?
 *
 * KENAPA DIHITUNG DARI TANGGAL, BUKAN DARI KOLOM STATUS
 * "Sedang berlangsung" adalah satu-satunya keadaan yang tahu sendiri kapan
 * ia mulai dan kapan berakhir. Kalau dibuat sebagai status ke-5 yang harus
 * diklik panitia, ia hampir pasti akan lupa diubah — dan yang terlihat
 * pengunjung adalah "Segera Hadir" pada acara yang partainya sudah jalan
 * dari pagi. Tanggal mulai dan selesai sudah wajib diisi saat acara dibuat,
 * jadi datanya selalu ada.
 *
 * Perbandingan memakai batas HARI, bukan jam. Acara yang dijadwalkan
 * "18 Agustus" dianggap berlangsung sejak pukul 00:00 tanggal itu sampai
 * 23:59 tanggal terakhir — bukan sejak jam tayang yang kebetulan tersimpan
 * di basis data.
 */
export function sedangBerlangsung(turnamen) {
  const mulai = turnamen?.start_date ? new Date(turnamen.start_date) : null;
  if (!mulai || Number.isNaN(mulai.getTime())) return false;

  const selesai = turnamen?.end_date ? new Date(turnamen.end_date) : mulai;
  if (Number.isNaN(selesai.getTime())) return false;

  const hariIni = new Date();
  const awalHariIni = new Date(hariIni.getFullYear(), hariIni.getMonth(), hariIni.getDate());

  const awal = new Date(mulai.getFullYear(), mulai.getMonth(), mulai.getDate());
  const akhir = new Date(selesai.getFullYear(), selesai.getMonth(), selesai.getDate());

  return awalHariIni >= awal && awalHariIni <= akhir;
}

/**
 * Menentukan tombol apa yang pantas muncul pada kartu sebuah turnamen.
 *
 * Satu tombol per keadaan, bukan semua tombol sekaligus. Kartu yang memuat
 * "Daftar", "Info", "Tanding", dan "Seni" berbarengan memaksa pembaca
 * memilih, padahal pada setiap keadaan hanya ada satu hal yang masuk akal
 * dilakukan.
 *
 *   berlangsung  -> jadwal tanding & seni   (yang dicari orang: partai saya kapan)
 *   dibuka       -> daftar                  (pendaftaran sedang menerima)
 *   segera hadir -> info acara              (belum ada yang bisa dilakukan)
 *   selesai      -> jadwal & hasil          (arsip)
 */
export function aksiTurnamen(turnamen) {
  const info = infoStatus(turnamen);

  // Acara yang sudah selesai tidak bisa "sedang berlangsung", meski
  // tanggalnya bertumpuk karena salah input.
  if (info.status === STATUS.SELESAI) return 'jadwal';

  if (sedangBerlangsung(turnamen)) return 'jadwal';
  if (info.bisaDaftar) return 'daftar';

  return 'info';
}

export default {
  STATUS,
  normalkanStatus,
  infoStatus,
  hanyaTerlihat,
  sedangBerlangsung,
  aksiTurnamen,
};
